import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GoldButton, Card, EmptyState, ui } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const blank = { supplier: "", amount: "", currency: "AUD", issue_date: today(), due_date: "", status: "unpaid", description: "" };

export default function Invoices() {
  const { user } = useAuth();
  const cur = user?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...blank, currency: cur }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };
  const save = async () => {
    if (!form.supplier) return toast.error("Supplier is required");
    const payload = { ...form, amount: Number(form.amount || 0) };
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Updated" : "Added"); setOpen(false); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/invoices/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="Track supplier invoices and what's due."
        action={<GoldButton data-testid="add-invoice-btn" onClick={openNew}><Plus size={16} /> Add Invoice</GoldButton>} />

      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <EmptyState icon={FileText} title="No supplier invoices yet" hint="Add an invoice, or scan one from your Financial Inbox." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="text-xs uppercase tracking-[0.1em] text-[#A99F90] border-b border-[#EAE0D2]">
                  <th className="text-left py-4 px-6 font-normal">Supplier</th>
                  <th className="text-left py-4 px-3 font-normal">Issued</th>
                  <th className="text-left py-4 px-3 font-normal">Due</th>
                  <th className="text-left py-4 px-3 font-normal">Status</th>
                  <th className="text-right py-4 px-3 font-normal">Amount</th>
                  <th className="text-right py-4 px-6 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-[#F0E9DD] hover:bg-white/60" data-testid={`invoice-row-${it.id}`}>
                    <td className="py-4 px-6 text-[#2C2A28]"><div>{it.supplier}</div>{it.description && <span className="text-xs text-[#A99F90] line-clamp-1">{it.description}</span>}</td>
                    <td className="py-4 px-3 text-[#8A7F70] font-mono-data text-xs">{it.issue_date}</td>
                    <td className="py-4 px-3 text-[#8A7F70] font-mono-data text-xs">{it.due_date || "—"}</td>
                    <td className="py-4 px-3">
                      <Badge className={it.status === "paid" ? "bg-[#8FB996]/15 text-[#5a9a6a] border-[#8FB996]/30" : "bg-[#C6A45C]/15 text-[#8a6d2f] border-[#C6A45C]/30"}>{it.status}</Badge>
                    </td>
                    <td className="py-4 px-3 text-right font-mono-data text-[#2C2A28]">{fmt(it.amount, it.currency || cur)}</td>
                    <td className="py-4 px-6 text-right">
                      <button data-testid={`edit-invoice-${it.id}`} onClick={() => openEdit(it)} className="text-[#8A7F70] hover:text-[#C6A45C] p-1.5"><Pencil size={15} /></button>
                      <button data-testid={`delete-invoice-${it.id}`} onClick={() => remove(it.id)} className="text-[#8A7F70] hover:text-[#C99FA0] p-1.5"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={ui.dialog}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">{editing ? "Edit Invoice" : "New Supplier Invoice"}</DialogTitle>
            <DialogDescription className="text-[#8A7F70]">Keep your payables organised.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className={ui.label}>Supplier</Label><Input data-testid="invoice-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className={ui.input} placeholder="Acme Pty Ltd" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={ui.label}>Amount</Label><Input data-testid="invoice-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={ui.input} placeholder="0.00" /></div>
              <div>
                <Label className={ui.label}>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="invoice-currency" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                  <SelectContent className={ui.selectContent}>{["AUD", "NZD", "USD", "EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={ui.label}>Issue date</Label><Input data-testid="invoice-issue" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className={ui.input} /></div>
              <div><Label className={ui.label}>Due date</Label><Input data-testid="invoice-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={ui.input} /></div>
            </div>
            <div>
              <Label className={ui.label}>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="invoice-status" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                <SelectContent className={ui.selectContent}><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className={ui.label}>Description</Label><Input data-testid="invoice-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={ui.input} placeholder="Optional" /></div>
          </div>
          <DialogFooter>
            <GoldButton data-testid="save-invoice-btn" onClick={save} className="w-full">{editing ? "Save Changes" : "Save Invoice"}</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
